import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { GetAllStudentsAPI } from "@/services/api/student/getAllStudents";
import { GetAllBusinessesAPI } from "@/services/api/business/getAllBusinesses";
import { GetAllSocietiesAPI } from "@/services/api/society/getAllSocieties";
import { GetAllSponsorActivitiesAPI } from "@/services/api/sponsor/getAllSponsorActivities";
import AdminStatCard from "@/components/AdminStatCard";
import AdminListSkeleton from "@/components/AdminListSkeleton";
import AdminEmptyState from "@/components/AdminEmptyState";

/**
 * Dashboard: summary counts pulled from the same admin APIs used by the
 * individual management screens.
 */
const DashboardPage: React.FC = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [students, setStudents] = useState<any[]>([]);
  const [businesses, setBusinesses] = useState<any[]>([]);
  const [societies, setSocieties] = useState<any[]>([]);
  // The societies request is deliberately capped (`size: 5`) because it only
  // feeds the "Recent Societies" list, so its array length is NOT the total.
  // `getAllSocieties` returns a server-side `total` for the stat card.
  const [societiesTotal, setSocietiesTotal] = useState(0);
  const [activities, setActivities] = useState<any[]>([]);

  const fetchAll = async () => {
    setLoading(true);
    setError(null);
    const [sResp, bResp, socResp, actResp] = await Promise.all([
      GetAllStudentsAPI(),
      GetAllBusinessesAPI(),
      GetAllSocietiesAPI({ page: 1, size: 5 }),
      GetAllSponsorActivitiesAPI(1),
    ]);
    if (sResp.status === 0 && Array.isArray(sResp.resp)) setStudents(sResp.resp);
    if (bResp.status === 0 && Array.isArray(bResp.resp)) setBusinesses(bResp.resp);
    if (socResp.status === 0 && socResp.resp?.data) {
      setSocieties(socResp.resp.data);
      setSocietiesTotal(socResp.resp.total ?? socResp.resp.data.length);
    }
    if (actResp.status === 0 && Array.isArray(actResp.resp)) setActivities(actResp.resp);
    if (sResp.status !== 0 && bResp.status !== 0 && socResp.status !== 0 && actResp.status !== 0) {
      setError(t("Something went wrong"));
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchAll();
  }, []);

// "Pending Verification" means a student actually submitted a document and it
// is still waiting on an admin — the same definition the User List's
// `pendingCount` uses, so the two screens can never disagree.
//
// Deriving it from document evidence, not `student_profile.verified`: 997 of
// 1002 rows carry `verified = true` with no document at all (seeded straight
// into the DB, bypassing upload+approve), so the flag reports ~5 where the real
// queue depth is 3. A student who uploaded nothing is "no documents", not
// "pending". Empty string counts as absent here, matching the API predicate —
// 159 profiles store `student_card_image_url = ""`.
const hasDocument = (value: unknown) =>
  value !== null && value !== undefined && String(value).trim() !== "";
// "Pending Verification" = at least one submitted document still awaiting
// review. Matches the User List's server-side `pendingCount` exactly, so the two
// screens can never disagree. Note this is per-document: a student with an
// approved card but an unapproved HKID is still in the queue.
//
// Deriving it from document evidence, not `student_profile.verified`: 997 of
// 1002 rows carry `verified = true` with no document at all (seeded straight
// into the DB, bypassing upload+approve), so the flag reports ~5 where the real
// queue depth is far smaller. Empty string counts as absent, matching the API
// predicate — 159 profiles store `student_card_image_url = ""`.
const pendingVerification = students.filter(
  (u) =>
    (hasDocument(u.student_profile?.student_card_image_url) &&
      !hasDocument(u.student_profile?.verified_student_card_image_url)) ||
    (hasDocument(u.student_profile?.hkid_image_url) &&
      !hasDocument(u.student_profile?.verified_hkid_image_url)),
).length;

  return (
    <div className="p-6">
      <h1 className="mb-6 text-2xl font-bold text-[#222]">{t("Dashboard")}</h1>

      {loading ? (
        <AdminListSkeleton count={3} />
      ) : error ? (
        <AdminEmptyState
          iconType="MaterialCommunityIcons"
          iconName="cloud-alert-outline"
          title={t("Something went wrong")}
          message={error}
          actionLabel={t("Retry")}
          onAction={fetchAll}
        />
      ) : (
        <>
          <div className="mb-6 flex flex-wrap gap-3">
            <AdminStatCard
              label={t("Total Students")}
              value={students.length}
              iconType="MaterialCommunityIcons"
              iconName="school"
              iconColor="#2196f3"
              accentColor="#E3F2FD"
            />
            <AdminStatCard
              label={t("Pending Verification")}
              value={pendingVerification}
              iconType="MaterialCommunityIcons"
              iconName="shield-alert-outline"
              iconColor="#e65100"
              accentColor="#FFF3E0"
            />
            <AdminStatCard
              label={t("Businesses")}
              value={businesses.length}
              iconType="MaterialCommunityIcons"
              iconName="domain"
              iconColor="#7c4dff"
              accentColor="#EDE7F6"
            />
            <AdminStatCard
              label={t("Societies")}
              value={societiesTotal}
              iconType="MaterialCommunityIcons"
              iconName="account-group"
              iconColor="#2196f3"
              accentColor="#E3F2FD"
            />
            <AdminStatCard
              label={t("Sponsor Activities")}
              value={activities.length}
              iconType="MaterialCommunityIcons"
              iconName="hand-coin-outline"
              iconColor="#e65100"
              accentColor="#FFF3E0"
            />
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="rounded-xl bg-white p-5 shadow-sm">
              <h2 className="mb-3 text-base font-semibold text-[#222]">
                {t("Recent Societies")}
              </h2>
              {societies.length === 0 ? (
                <div className="text-sm text-[#888]">{t("No societies to review")}</div>
              ) : (
                <ul className="divide-y divide-gray-100">
                  {societies.slice(0, 5).map((s) => (
                    <li key={s.id} className="flex items-center justify-between py-2">
                      <span className="truncate text-sm text-[#333]">{s.name}</span>
                      <span className="text-xs text-[#888]">
                        {s.memberCount} {t("Members")}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="rounded-xl bg-white p-5 shadow-sm">
              <h2 className="mb-3 text-base font-semibold text-[#222]">
                {t("Sponsor Activities")}
              </h2>
              {activities.length === 0 ? (
                <div className="text-sm text-[#888]">
                  {t("No sponsor activities to review")}
                </div>
              ) : (
                <ul className="divide-y divide-gray-100">
                  {activities.slice(0, 5).map((a) => (
                    <li key={a.id} className="flex items-center justify-between py-2">
                      <span className="truncate text-sm text-[#333]">
                        {a.activity_name || "-"}
                      </span>
                      <span className="text-xs text-[#888]">
                        {(a.completions || []).length} {t("Tasks")}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default DashboardPage;